import { IResponsesDB } from 'interfaces/response.interface';

type Item = { accepted?: number | string | boolean; number?: number | string; [k: string]: any };
type Personnel = { id?: string | number; items?: Item[]; filled?: number; [k: string]: any };
type Ambulance = { id?: string | number; items?: Item[]; filled?: number; [k: string]: any };
type Request = {
  id?: string | number;
  requests?: {
    id?: string | number;
    ambulance?: Ambulance[];
    personnel?: Personnel[];
    [k: string]: any;
  };
  [k: string]: any;
};

/**
 * For each response -> requests.ambulance/personnel entry, find a matching id
 * among all requests.ambulance/personnel entries, compute `filled` from that request's
 * items (sum of item.number where item.accepted == 1) and copy into response.
 *
 * Returns a new responses array (input arrays are not mutated).
 */
export function syncFilledFromRequests(
  responses: IResponsesDB[] = [],
  requests: Request[] = [],
): IResponsesDB[] {
  if (!Array.isArray(requests) || !Array.isArray(responses)) return [];

  // Build ambulanceId -> filled map from requests
  const ambFilled = new Map<string, number>();
  // Build personnelId -> filled map from requests
  const persFilled = new Map<string, number>();

  for (const request of requests) {
    if (!Array.isArray(request.ambulance)) continue;
    for (const amb of request.ambulance) {
      const key = amb?.id != null ? String(amb.id) : '';
      if (!key) continue;
      const items = Array.isArray(amb.items) ? amb.items : [];
      const filled = items.reduce((sum: number, it: Item) => {
        if (it != null && Number(it.accepted) === 1) {
          return sum + (Number(it.number) || 0);
        }
        return sum;
      }, 0);
      ambFilled.set(key, (ambFilled.get(key) || 0) + filled);
    }

    if (!Array.isArray(request.personnel)) continue;
    for (const pers of request.personnel) {
      const key = pers?.id != null ? String(pers.id) : '';
      if (!key) continue;
      const items = Array.isArray(pers.items) ? pers.items : [];
      const filled = items.reduce((sum: number, it: Item) => {
        if (it != null && Number(it.accepted) === 1) {
          return sum + (Number(it.number) || 0);
        }
        return sum;
      }, 0);
      persFilled.set(key, (persFilled.get(key) || 0) + filled);
    }
  }

  // Deep clone responses to avoid mutating original
  const responseClone: IResponsesDB[] = JSON.parse(JSON.stringify(responses));

  for (const response of responseClone) {
    const responseAmb = response?.requests?.ambulance;
    if (Array.isArray(responseAmb)) {
      for (const ra of responseAmb) {
        const key = ra?.id != null ? String(ra.id) : '';
        if (!key) continue;
        const filled = ambFilled.get(key);
        if (filled !== undefined) {
          ra.filled = filled;
        }
      }
    }

    const responsePers = response?.requests?.personnel;
    if (Array.isArray(responsePers)) {
      for (const rp of responsePers) {
        const key = rp?.id != null ? String(rp.id) : '';
        if (!key) continue;
        const filled = persFilled.get(key);
        if (filled !== undefined) {
          rp.filled = filled;
        }
      }
    }
  }

  return responseClone;
}
